import {
  signIn,
  signUp,
  confirmSignUp,
  confirmSignIn,
  resendSignUpCode,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  signOut as amplifySignOutFn
} from 'aws-amplify/auth';
import { federateWithToken, clearFederation } from './credentials-provider.js';

// Auth helpers (OTP, social, password flows) via Amplify v6.
// Each sign-in/session function returns a credentialsShim for legacy AWS SDK v2 callers.

// Initiate passwordless email login (triggers OTP via Lambda)
export async function passwordlessSignIn(email) {
  return signIn({
    username: email,
    options: { authFlowType: 'CUSTOM_WITHOUT_SRP' }
  });
}

// Submit OTP code, return session + credentialsShim
export async function passwordlessConfirmOTP(code) {
  const result = await confirmSignIn({ challengeResponse: code });

  if (!result.isSignedIn) {
    // Still has a next step (shouldn't happen in normal OTP flow)
    throw new Error(`Unexpected auth step: ${result.nextStep?.signInStep}`);
  }

  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload ?? {};

  const credentialsShim = {
    accessKeyId: session.credentials?.accessKeyId,
    secretAccessKey: session.credentials?.secretAccessKey,
    sessionToken: session.credentials?.sessionToken,
    identityId: session.identityId,
    _identityId: session.identityId,
    get(callback) {
      callback(null);
    }
  };

  return {
    isSignedIn: true,
    credentialsShim,
    identityId: session.identityId,
    email: payload.email ?? '',
    nickname: payload.nickname ?? payload.email ?? ''
  };
}

// Check current session validity. Returns credentialsShim + user info, or throws.
export async function checkAmplifySession() {
  const session = await fetchAuthSession();

  if (!session.tokens?.idToken) {
    throw new Error('No active Amplify session');
  }

  const payload = session.tokens.idToken.payload ?? {};

  const credentialsShim = {
    accessKeyId: session.credentials?.accessKeyId,
    secretAccessKey: session.credentials?.secretAccessKey,
    sessionToken: session.credentials?.sessionToken,
    identityId: session.identityId,
    _identityId: session.identityId,
    get(callback) {
      callback(null);
    }
  };

  return {
    credentialsShim,
    identityId: session.identityId,
    email: payload.email ?? '',
    nickname: payload.nickname ?? payload.email ?? ''
  };
}

// Federate with a social-provider token and resolve Identity Pool credentials
export async function resolveSocialSession(logins) {
  federateWithToken(logins);
  const session = await fetchAuthSession({ forceRefresh: true });

  const credentialsShim = {
    accessKeyId: session.credentials?.accessKeyId,
    secretAccessKey: session.credentials?.secretAccessKey,
    sessionToken: session.credentials?.sessionToken,
    identityId: session.identityId,
    _identityId: session.identityId,
    get(callback) {
      callback(null);
    }
  };

  return {
    credentialsShim,
    identityId: session.identityId
  };
}

// Random password for signUp - Cognito requires one even for passwordless pools.
function generateRandomPassword() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  let pwd = '';
  for (const b of arr) pwd += chars[b % chars.length];
  // Guarantee at least one of each required class
  return 'Aa1!' + pwd;
}

// Register a new user. Cognito sends a verification code to the email
export async function passwordlessSignUp({ email, nickname, locale, deviceName = 'web', rtTarget = 'world' }) {
  return signUp({
    username: email,
    password: generateRandomPassword(),
    options: {
      userAttributes: {
        email,
        nickname,
        locale,
        'custom:deviceName': deviceName,
        'custom:rt_target': rtTarget
      }
    }
  });
}

// Confirm signup with the verification code
export async function passwordlessConfirmSignUp(email, code) {
  return confirmSignUp({ username: email, confirmationCode: code });
}

// Trigger a new signup confirmation message for an unconfirmed user.
export async function resendSignUpConfirmationCode(email) {
  return resendSignUpCode({ username: email });
}

// Initiate forgot-password flow (sends verification code)
export async function amplifyForgotPassword(username, clientMetadata) {
  const options = clientMetadata ? { clientMetadata } : undefined;
  return amplifyResetPassword({ username, options });
}

// Complete forgot-password with verification code + new password
export async function amplifyConfirmForgotPassword(username, confirmationCode, newPassword) {
  return amplifyConfirmResetPassword({ username, confirmationCode, newPassword });
}

// Sign out and clear federation state
export async function amplifySignOut() {
  clearFederation();
  try {
    await amplifySignOutFn();
  } catch {} // Amplify throws if no session exists safe to ignore
}
